/**
 * NatureNest Scroll Zoom
 * Smooth scroll-scrub zoom effect (inspired by rhodeskin.com).
 * Images are zoomed based on vertical viewport position:
 * higher in viewport = more zoomed out, lower in viewport = more zoomed in.
 * Excludes PDP product gallery images.
 * No dependencies - vanilla JS with requestAnimationFrame throttling.
 */
(function () {
  'use strict';

  // Temporary global kill switch while scroll-linked image motion is under review.
  var DISABLE_NN_SCROLL_ZOOM = true;

  var targets = [];
  var rafId = null;
  var refreshTimer = null;
  var lastRefreshAt = 0;
  var SCALE_MAX = 1.18;
  var SCALE_MIN = 1.0;
  var MIN_TARGET_WIDTH = 120;
  var MIN_TARGET_HEIGHT = 120;
  var MIN_TARGET_AREA = 20000;

  // Visual image selectors to animate (element -> closest section wrapper for center tracking).
  // Includes legacy about/image-banner/instagram sections used in current templates.
  var SELECTORS = [
    { sel: '.sections-slide-show .slide-image img', parent: '.sections-slide-show .item' },
    { sel: '.shopify-section .image-zoom img', parent: '.image-zoom' },
    { sel: '.shopify-section .card-media img.motion-reduce', parent: '.card-media' },
    { sel: '.nn-banner-overlay__image', parent: '.nn-banner-overlay' },
    { sel: '.nn-hero-overlay__image', parent: '.nn-hero-overlay' },
    { sel: '.nn-image-banner-wrapper .section-block-banner-image .img-box img, .nn-image-banner-wrapper-left .section-block-banner-image .img-box img', parent: '.section-block-left' },
    { sel: '.nn-image-gallery__item img', parent: '.nn-image-gallery' },
    { sel: '.nn-founders__img-box img', parent: '.nn-founders' },
    { sel: '.nn-about-founders-photo__img', parent: '.nn-about-founders-photo' },
    { sel: '.nn-product-obsession__photo img', parent: '.nn-product-obsession' },
    { sel: '.nn-feature-grid__image img', parent: '.nn-feature-grid' },
    { sel: '.nn-text-block__img', parent: '.nn-text-block' },
    { sel: '.nn-text-badges__img', parent: '.nn-text-badges' },
    { sel: '.nn-trial-guarantee__img', parent: '.nn-trial-guarantee' },
    { sel: '#MainContent .about-row .image-zoom img', parent: '.about-row' },
    { sel: '#MainContent .halo-banner.image-banner .section-block-banner-image .img-box img', parent: '.halo-banner.image-banner' },
    { sel: '#MainContent .instagram-slide .halo-item .image-zoom img', parent: '.instagram-slide' },
    // Fallback: include any large section image on supported templates.
    { sel: '#MainContent .shopify-section img, .template-page > .shopify-section img, .template-product > .shopify-section img, .template-index > .shopify-section img', parent: 'img' }
  ];

  var EXCLUDED_ANCESTORS = [
    '.productView',
    '.productView-thumbnail',
    '.productView-thumbnail-wrapper',
    '.nn-comparison-table',
    '.nn-feature-icons',
    '.nn-text-badges__card',
    '[id*="nn-sust-system"]',
    '.nn-trust-bar',
    '.policies-icon',
    '.nn-trial-guarantee__top-icon',
    '.nn-trial-guarantee__signature',
    '.footer',
    // Daniel meeting note 2026-04-10: disable scroll-zoom on Instagram
    // sections (mobile + desktop) so the grid stays stable.
    '.instagram-slide',
    '.instagram-special',
    '.instagram-grid-section'
  ];

  function hasElement(items, el) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].el === el) return true;
    }
    return false;
  }

  function resetRemovedTargets(previous, next) {
    for (var i = 0; i < previous.length; i++) {
      var oldTarget = previous[i];
      if (!hasElement(next, oldTarget.el)) {
        oldTarget.el.style.transform = '';
        oldTarget.el.style.willChange = '';
        oldTarget.el.style.transformOrigin = '';
      }
    }
  }

  function isExcludedTarget(el) {
    if (el.closest('[data-nn-scroll-zoom="off"], .nn-scroll-zoom-off')) return true;

    for (var i = 0; i < EXCLUDED_ANCESTORS.length; i++) {
      if (el.closest(EXCLUDED_ANCESTORS[i])) return true;
    }

    return false;
  }

  function isLargeVisual(el) {
    var rect = el.getBoundingClientRect();
    var width = rect.width;
    var height = rect.height;
    var area = width * height;

    if (width < MIN_TARGET_WIDTH) return false;
    if (height < MIN_TARGET_HEIGHT) return false;
    if (area < MIN_TARGET_AREA) return false;

    return true;
  }

  function collectTargets() {
    var previousTargets = targets.slice();
    var nextTargets = [];
    var seen = [];

    for (var i = 0; i < SELECTORS.length; i++) {
      var cfg = SELECTORS[i];
      document.querySelectorAll(cfg.sel).forEach(function (el) {
        if (seen.indexOf(el) !== -1) return;
        if (isExcludedTarget(el)) return;
        if (!isLargeVisual(el)) return;

        var parent = el.closest(cfg.parent);
        if (!parent) return;

        // Ensure overflow hidden on the direct image wrapper.
        if (el.parentElement) {
          el.parentElement.style.overflow = 'hidden';
        }

        seen.push(el);
        nextTargets.push({ el: el, parent: parent });
      });
    }

    targets = nextTargets;
    resetRemovedTargets(previousTargets, nextTargets);
  }

  function prepareTargets() {
    for (var i = 0; i < targets.length; i++) {
      targets[i].el.style.willChange = 'transform';
      targets[i].el.style.transformOrigin = 'center center';
    }
  }

  function clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
  }

  function update() {
    rafId = null;
    var vh = window.innerHeight;

    for (var i = 0; i < targets.length; i++) {
      var t = targets[i];
      var rect = t.parent.getBoundingClientRect();
      var sectionCenter = rect.top + rect.height * 0.5;
      // Viewport position model:
      // top (0) => zoomed out, bottom (1) => zoomed in.
      var pos = clamp(sectionCenter / vh, 0, 1);
      var scale = SCALE_MIN + (SCALE_MAX - SCALE_MIN) * pos;

      t.el.style.transform = 'scale(' + scale.toFixed(4) + ')';
    }
  }

  function onScroll() {
    if (!rafId) rafId = requestAnimationFrame(update);
  }

  function scheduleRefresh() {
    var now = Date.now();
    // Throttle refresh storms from rapid lazyload/image load events.
    if (refreshTimer || now - lastRefreshAt < 220) return;
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      lastRefreshAt = Date.now();
      collectTargets();
      prepareTargets();
      onScroll();
    }, 140);
  }

  function onResize() {
    collectTargets();
    prepareTargets();
    onScroll();
  }

  function init() {
    if (DISABLE_NN_SCROLL_ZOOM) return;

    // Skip in Shopify theme editor
    if (window.Shopify && window.Shopify.designMode) return;

    collectTargets();
    if (targets.length === 0) return;

    prepareTargets();
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', onResize);
    // Refresh target collection when lazy-loaded images become available/sized.
    document.addEventListener('load', function (event) {
      if (event && event.target && event.target.tagName === 'IMG') {
        scheduleRefresh();
      }
    }, true);
    document.addEventListener('lazyloaded', scheduleRefresh, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
